import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Text, Button } from 'components';
import { exchangeLineAuthCodeForToken } from 'api';
import {
  TEXT_STRING,
  routes,
} from '../../constants';
import { TLineOAuthTokenBody } from 'types';
import { useLine, useToast } from 'hooks';

const lineStr = TEXT_STRING.LINE;

enum ELineErrorCode {
  INVALID_REQUEST = 'INVALID_REQUEST',
  ACCESS_DENIED = 'ACCESS_DENIED',
  UNSUPPORTED_RESPONSE_TYPE = 'UNSUPPORTED_RESPONSE_TYPE',
  INVALID_SCOPE = 'INVALID_SCOPE',
  SERVER_ERROR = 'SERVER_ERROR',
  LOGIN_REQUIRED = 'LOGIN_REQUIRED',
  INTERACTION_REQUIRED = 'INTERACTION_REQUIRED',
}

const LINE_ERROR_MESSAGES: Record<ELineErrorCode, string> = {
  INVALID_REQUEST: lineStr.ERROR.CODES.INVALID_REQUEST,
  ACCESS_DENIED: lineStr.ERROR.CODES.ACCESS_DENIED,
  UNSUPPORTED_RESPONSE_TYPE: lineStr.ERROR.CODES.UNSUPPORTED_RESPONSE_TYPE,
  INVALID_SCOPE: lineStr.ERROR.CODES.INVALID_SCOPE,
  SERVER_ERROR: lineStr.ERROR.CODES.SERVER_ERROR,
  LOGIN_REQUIRED: lineStr.ERROR.CODES.LOGIN_REQUIRED,
  INTERACTION_REQUIRED: lineStr.ERROR.CODES.INTERACTION_REQUIRED,
};

const isLineErrorCode = (v: string | null): v is ELineErrorCode => {
  if (!v) return false;
  return (Object.values(ELineErrorCode) as string[]).includes(v);
};

function LineCallback() {
  // ---- refs ----
  const isFired = useRef<boolean>(false);
  // ---- hooks ----
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { applyLineSessionResponse } = useLine();
  const { toastError } = useToast();

  // ---- query params ----
  const codeParam = searchParams.get('code');
  const stateParam = searchParams.get('state');
  const errorParam = searchParams.get('error');
  const errorCode = isLineErrorCode(errorParam) ? errorParam : null;
  const errorMessage = errorCode ? LINE_ERROR_MESSAGES[errorCode] : null;

  // ---- LOGIN: code -> app session ----
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (body: TLineOAuthTokenBody) => exchangeLineAuthCodeForToken(body),
    onSuccess: async (data) => {
      await applyLineSessionResponse(data);
    },
    onError: (e: unknown) => {
      toastError(e as Error, { position: 'top-center' });
    },
    retry: 0,
  });

  useEffect(() => {
    if (window.location.search) {
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    (async () => {
      if (isFired.current) return;
      isFired.current = true;

      if (errorMessage || !codeParam || !stateParam) {
        navigate(routes.LOGIN, { replace: true });
        return;
      }

      try {
        const redirectUri = `${window.location.origin}${window.location.pathname}`;

        await mutateAsync({ code: codeParam, redirectUri, state: stateParam });
      } catch {
        isFired.current = false;
      }
    })();
  }, [codeParam, stateParam, errorMessage, mutateAsync, navigate]);

  const handleRetry = () => {
    navigate(routes.LOGIN, { replace: true });
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center px-6">
      <div className="text-center w-full max-w-md">
        {isPending && !errorMessage && (
          <>
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto mb-4" />
            <Text fontSize={16} bold>
              {lineStr.LOADING.TITLE}
            </Text>
            <Text fontSize={14} textColor="text-neutral-500" className="mt-2">
              {lineStr.LOADING.DESCRIPTION}
            </Text>
          </>
        )}
        {errorMessage && (
          <>
            <Text fontSize={16} bold>
              {lineStr.ERROR.TITLE}
            </Text>

            <Text fontSize={14} textColor="text-neutral-500" className="mt-2">
              {errorMessage}
            </Text>
            <div className="mt-6 flex flex-col gap-3">
              <Button block onClick={handleRetry}>
                {lineStr.ACTIONS.RETRY}
              </Button>
              <Button
                block
                bgColor="bg-white"
                textColor="text-primary"
                onClick={() => navigate('/', { replace: true })}
              >
                {lineStr.ACTIONS.BACK}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default LineCallback;
